import React, { useState } from 'react';
import { Row, Col, Button } from 'antd';
import { withTranslation } from 'react-i18next';
import PlayoutsLabelContainer from '../../containers/step2_settings/PlayoutsLabelContainer';
import HiddenByCpmTypeContainer from '../../../../shared/containers/HiddenByCpmTypeContainer';
import {
  ScreenList,
  ScreenListCard,
  ScreenListItem,
  ScreenInfo,
  ScreenPrice,
  SectionTitle,
  StyledModal,
  ScreenName,
  ScreenType,
  SpotButton,
  ModalTitle,
  ModalInfoRow,
  ModalInfoLabel,
  ModalInfoValue,
  ModalImagePlaceholder,
  ModalResumeText,
  MapSectionCard
} from '../../styles/checkoutStyles';
import MapComponent from './MapComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getSimilarAspectRatio } from '../../../../../services/display';
import ListPagination, { getPageItems, getLastPage } from '../../../../shared/components/ListPagination';

const SCREENS_PAGE_SIZE = 100;

const MapAndScreensList = ({ campaign, pricesData, cart, t }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [page, setPage] = useState(1);

  const cartMap = {};
  if (cart) {
    cart.forEach(d => {
      cartMap[d.id] = d;
    });
  }

  const groupByDimension = display => {
    const cartDisplay = cartMap[display.display_id] || {};
    const width = cartDisplay.resolution_width || display.display_resolution_width;
    const height = cartDisplay.resolution_height || display.display_resolution_height;
    const indice = getSimilarAspectRatio(width, height, t);
    return indice;
  };

  const showModal = screen => {
    setSelectedScreen(screen);
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setSelectedScreen(null);
  };

  // Group contents_displays by unique screen (display_id).
  const priceByDisplay = {};
  if (pricesData && pricesData.items) {
    pricesData.items.forEach(item => {
      const id = item.display_id;
      priceByDisplay[id] = (priceByDisplay[id] || 0) + (parseFloat(item.paid_amount_with_profit) || 0);
    });
  }

  const displayMap = {};
  if (campaign.contents_displays) {
    campaign.contents_displays.forEach(cd => {
      const displayId = cd.display_id || cd.id;
      if (!displayMap[displayId]) {
        displayMap[displayId] = {
          ...cd,
          display_id: displayId,
          total_promised_shows: cd.promised_shows || 0,
          paid_amount_with_profit: parseFloat(cd.paid_amount_with_profit) || 0,
          contents_count: 1
        };
      } else {
        displayMap[displayId].total_promised_shows += cd.promised_shows || 0;
        displayMap[displayId].paid_amount_with_profit += parseFloat(cd.paid_amount_with_profit) || 0;
        displayMap[displayId].contents_count += 1;
      }
    });
    // Los totales autoritativos del backend ganan cuando existen
    Object.keys(displayMap).forEach(id => {
      if (priceByDisplay[id] !== undefined) {
        displayMap[id].paid_amount_with_profit = priceByDisplay[id];
      }
    });
  }

  const displays = Object.values(displayMap);
  const displayCount = displays.length;
  const currentPage = Math.min(page, getLastPage(displayCount, SCREENS_PAGE_SIZE));
  const currency = pricesData ? pricesData.currency : campaign.currency;

  return (
    <MapSectionCard>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <SectionTitle>{t('Map')}</SectionTitle>
          <MapComponent displays={campaign.contents_displays} />
        </Col>
        <Col xs={24} lg={12}>
          <SectionTitle>
            {t('Screen list ({count})')} ({displayCount})
          </SectionTitle>
          <ScreenListCard>
            <ScreenList
              key={currentPage}
              dataSource={getPageItems(displays, currentPage, SCREENS_PAGE_SIZE)}
              renderItem={item => (
                <ScreenListItem>
                  <ScreenInfo>
                    <ScreenName>{item.display_name}</ScreenName>
                    <ScreenType>
                      {item.display_location_type} - {groupByDimension(item)}
                    </ScreenType>
                  </ScreenInfo>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <SpotButton onClick={() => showModal(item)}>
                      <span>{t('Spot')}</span>
                      <FontAwesomeIcon icon={faEye} />
                    </SpotButton>
                    <ScreenPrice>
                      {item.paid_amount_with_profit.toLocaleString('es-AR', {
                        style: 'currency',
                        currency: currency
                      })}
                    </ScreenPrice>
                  </div>
                </ScreenListItem>
              )}
            />
            <ListPagination
              total={displayCount}
              pageSize={SCREENS_PAGE_SIZE}
              current={currentPage}
              onChange={setPage}
              labelKey="Showing {{shown}} of {{total}} screens"
            />
          </ScreenListCard>
        </Col>
      </Row>

      {selectedScreen && (
        <StyledModal
          title={
            <ModalTitle>
              <Button
                key="back"
                onClick={handleCancel}
                style={{
                  border: 'none',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
              <ModalResumeText>{t('Summary')}</ModalResumeText>
            </ModalTitle>
          }
          visible={modalVisible}
          onCancel={handleCancel}
          footer={null}
          closeIcon={<FontAwesomeIcon icon={faTimes} />}
        >
          <div>
            <ModalInfoRow>
              <ModalInfoLabel>{t('Screen')}</ModalInfoLabel>
              <ModalInfoValue>{selectedScreen.display_name}</ModalInfoValue>
            </ModalInfoRow>

            <ModalInfoRow>
              <ModalInfoLabel>{t('Investment')}</ModalInfoLabel>
              <ModalInfoValue>
                {selectedScreen.paid_amount_with_profit.toLocaleString('es-AR', {
                  style: 'currency',
                  currency: currency
                })}
              </ModalInfoValue>
            </ModalInfoRow>

            <HiddenByCpmTypeContainer>
              <ModalInfoRow>
                <ModalInfoLabel><PlayoutsLabelContainer /></ModalInfoLabel>
                <ModalInfoValue>
                  {selectedScreen.total_promised_shows.toLocaleString('es-AR')}
                </ModalInfoValue>
              </ModalInfoRow>
            </HiddenByCpmTypeContainer>

            <ModalInfoRow>
              <ModalInfoLabel>{t('Number of ads on this screen')}</ModalInfoLabel>
              <ModalInfoValue>{selectedScreen.contents_count}</ModalInfoValue>
            </ModalInfoRow>

            <ModalImagePlaceholder>
              {selectedScreen.content_file ? (
                <img src={selectedScreen.content_file} alt={selectedScreen.content_name} />
              ) : (
                <span>1000x700</span>
              )}
            </ModalImagePlaceholder>
          </div>
        </StyledModal>
      )}
    </MapSectionCard>
  );
};

export default withTranslation()(MapAndScreensList);
